import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { resolveBackendImage } from '../../utils/brandLogo';

/**
 * Hook que carrega a logo do admin do tenant ativo.
 *
 * Resolve nesta ordem: `logo_admin` → `logo_principal` → null.
 * Retorna null quando o tenant ainda não configurou logo — o
 * consumidor decide o placeholder (NÃO existe mais fallback SVG).
 *
 * Reage à troca de município selecionado e a updates do evento global
 * `siteConfig:updated`.
 *
 * @param {string} [refreshKey] - opcional: força re-fetch quando muda
 *   (ex.: pathname). Útil pra recarregar após navegação.
 */
export function useAdminBrandLogo(refreshKey) {
  const [brandLogo, setBrandLogo] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadBrandLogo = () => {
      const params = {};
      let hasMunicipio = false;
      try {
        const saved = localStorage.getItem('admin_selected_municipio');
        if (saved && saved !== 'null' && saved !== 'global') {
          const parsed = JSON.parse(saved);
          if (parsed?.id) {
            params.municipio_id = parsed.id;
            hasMunicipio = true;
          }
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[useAdminBrandLogo] read municipio failed:', err.message);
        }
      }

      // Endpoint publico `/configuracoes` (nao exige auth) — evita 401 durante
      // race conditions de sessao e polui console DevTools. A logo e info publica.
      // Se municipio_id ausente, backend faz merge com config global.
      api
        .get('/configuracoes', { params: hasMunicipio ? params : {} })
        .then((res) => {
          if (cancelled) return;
          const cfg = res.data || {};
          const raw = cfg.logo_admin || cfg.logo_principal || '';
          // resolveBackendImage normaliza:
          //  - relative → absolute (com BACKEND_URL)
          //  - absolute apontando p/ /api/uploads → re-host pro BACKEND_URL atual
          setBrandLogo(resolveBackendImage(raw));
        })
        .catch(() => { if (!cancelled) setBrandLogo(null); });
    };

    loadBrandLogo();
    const onConfigUpdated = () => loadBrandLogo();
    window.addEventListener('siteConfig:updated', onConfigUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('siteConfig:updated', onConfigUpdated);
    };
  }, [refreshKey]);

  return brandLogo;
}

/**
 * Hook que retorna o número de itens pendentes na fila de moderação.
 * Refetch automático quando `refreshKey` muda (ex.: navegação).
 */
export function useModerationPending(refreshKey) {
  const [pending, setPending] = useState(0);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/v2/moderation/queue/stats')
      .then((res) => { if (!cancelled) setPending(res.data?.pending || 0); })
      .catch(() => { /* silent — não bloqueia o layout */ });
    return () => { cancelled = true; };
  }, [refreshKey]);

  return pending;
}
